import { ContactFormValues, ContactParams } from 'app/models/contact-params'
import cleanUUID from 'app/views/components/functions/cleanUUID'

/** The contact fields the form edits. */
const EDITABLE_FIELDS = [
  'contactType',
  'name',
  'email',
  'faxNumber',
  'phoneNumber',
  'preferredContactMethod',
  'dataRoomAccess',
  'isIndividualOrGroup',
] as const

type EditableField = (typeof EDITABLE_FIELDS)[number]

/** A field's starting value, exactly as ContactsForm prefills it. */
const prefilled = (original: ContactParams, field: EditableField) => {
  const value = original[field]
  if (field === 'dataRoomAccess') return value ?? false
  if (field === 'isIndividualOrGroup') return value ?? null
  return value ?? ''
}

/** Whether the user changed any field of a contact the form was prefilled with. */
export const hasContactChanged = (
  values: ContactFormValues,
  original: ContactParams,
) => EDITABLE_FIELDS.some(field => values[field] !== prefilled(original, field))

/**
 * The contacts a save should send: new ones (no id) and ones the user changed.
 *
 * Saving an existing contact is not free: an active contact's edit runs
 * `EditActive`, which turns it inactive until it is activated again. So a
 * contact the user did not touch must not be sent — the entity stepper used to
 * send every contact it listed and deactivated them all.
 */
export const contactsToSave = (
  values: ContactFormValues[],
  originals: ContactParams[],
) =>
  values.filter(value => {
    if (!value.id) return true
    const original = originals.find(
      o => o.id === value.id || cleanUUID(o.id) === value.id,
    )
    return !original || hasContactChanged(value, original)
  })
